import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import {
  InputSearchFilter,
  NgCommonModule,
  toSearchInput$,
  toSignal,
  TypedFormControl,
  TypedFormGroup,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import {
  Brand,
  Recalls,
  TreatmentConfiguration,
} from '@principle-theorem/principle-core';
import {
  DEFAULT_RECALL_PERIOD_IN_MONTHS,
  IBrand,
  IRecallSettings,
  ITreatmentCategory,
  ITreatmentConfiguration,
  RECALL_TRACK_LABELS,
  RecallTrack,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  IGroup,
  isSameRef,
  multiFilter,
  shareReplayCold,
  undeletedFilter,
  WithRef,
} from '@principle-theorem/shared';
import { intersectionWith, sortBy } from 'lodash';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith, switchMap, take } from 'rxjs/operators';

@Component({
  selector: 'pr-recall-settings',
  templateUrl: './recall-settings.component.html',
  styleUrls: ['./recall-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatAutocompleteModule,
    NgCommonModule,
    MatSlideToggleModule,
    NgxMatSelectSearchModule,
    InformationBoxComponent,
  ],
})
export class RecallSettingsComponent {
  recallTreatmentOptions: RecallTreatmentOptions;
  formGroup = new TypedFormGroup<IRecallSettings>({
    periodInMonths: new TypedFormControl(DEFAULT_RECALL_PERIOD_IN_MONTHS, {
      validators: [Validators.required, Validators.min(1)],
    }),
    treatmentRefs: new TypedFormControl([], {}),
    label: new TypedFormControl<string>('', {
      validators: [Validators.maxLength(50)],
    }),
  });

  disabled = input<boolean>(false);
  brand = input<WithRef<IBrand>>();
  settings = input<IRecallSettings>();
  track = input<RecallTrack>(RecallTrack.Primary);
  otherTrackTreatmentRefs = input<DocumentReference<ITreatmentConfiguration>[]>(
    []
  );
  otherTrack = input<RecallTrack | undefined>(undefined);
  settingsChange = output<IRecallSettings>();

  localTreatmentRefs: Signal<DocumentReference<ITreatmentConfiguration>[]>;
  overlapTreatments: Signal<DocumentReference<ITreatmentConfiguration>[]>;
  overlapTreatmentNames: Signal<string[]>;
  otherTrackLabel: Signal<string>;
  showOverlapWarning: Signal<boolean>;

  constructor() {
    this.recallTreatmentOptions = new RecallTreatmentOptions(
      toObservable(this.brand)
    );

    const treatmentRefsCtrl = this.formGroup.controls.treatmentRefs;
    this.localTreatmentRefs = toSignal(
      treatmentRefsCtrl.valueChanges.pipe(
        startWith(treatmentRefsCtrl.value),
        map((treatmentRefs) => treatmentRefs ?? [])
      ),
      []
    );

    this.overlapTreatments = computed(() =>
      intersectionWith(
        this.localTreatmentRefs(),
        this.otherTrackTreatmentRefs(),
        isSameRef
      )
    );

    this.overlapTreatmentNames = computed(() => {
      const overlapRefs = this.overlapTreatments();
      if (!overlapRefs.length) {
        return [];
      }
      const allTreatments = this.recallTreatmentOptions.treatments();
      return overlapRefs
        .map((ref) => {
          const treatment = allTreatments.find((option) =>
            isSameRef(option.ref, ref)
          );
          return treatment?.name ?? '';
        })
        .filter((name) => name.length);
    });

    this.otherTrackLabel = computed(() => {
      const track = this.otherTrack();
      return track !== undefined ? Recalls.trackLabel(this.brand(), track) : '';
    });

    this.showOverlapWarning = computed(
      () =>
        this.otherTrack() !== undefined && this.overlapTreatments().length > 0
    );

    effect(() => {
      if (this.disabled()) {
        this.formGroup.disable({ emitEvent: false });
        return;
      }
      this.formGroup.enable({ emitEvent: false });
    });

    toObservable(this.settings)
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((settings) => {
        this.formGroup.patchValue({
          ...(settings ?? {}),
          label: settings?.label ?? RECALL_TRACK_LABELS[this.track()],
        });
      });

    validFormGroupChanges$(this.formGroup)
      .pipe(takeUntilDestroyed())
      .subscribe((formValue) => {
        if (!this.disabled()) {
          this.settingsChange.emit(formValue);
        }
      });
  }
}

export class RecallTreatmentOptions {
  compareWithFn = isSameRef;
  searchCtrl = new TypedFormControl<string>('');
  treatments: Signal<WithRef<ITreatmentConfiguration>[]>;
  filteredTreatments: Signal<
    IGroup<WithRef<ITreatmentConfiguration>, string>[]
  >;

  constructor(brand$: Observable<WithRef<IBrand> | undefined>) {
    const categories$ = brand$.pipe(
      switchMap((brand) =>
        brand ? Firestore.getDocs(Brand.treatmentCategoryCol(brand)) : []
      ),
      multiFilter(undeletedFilter)
    );

    const treatments$ = brand$.pipe(
      switchMap((brand) =>
        brand ? Firestore.getDocs(TreatmentConfiguration.col(brand)) : []
      ),
      multiFilter(undeletedFilter),
      shareReplayCold()
    );

    this.treatments = toSignal(treatments$, []);

    const searchFilter = new InputSearchFilter(
      treatments$,
      toSearchInput$(this.searchCtrl),
      (option) => option.name
    );
    const filteredTreatments$ = combineLatest([
      categories$,
      searchFilter.results$,
    ]).pipe(
      map(([categories, options]) => this.toOptionGroups(categories, options))
    );
    this.filteredTreatments = toSignal(filteredTreatments$, []);
  }

  toOptionGroups(
    categories: WithRef<ITreatmentCategory>[],
    allOptions: WithRef<ITreatmentConfiguration>[]
  ): IGroup<WithRef<ITreatmentConfiguration>, string>[] {
    const groups = categories.map((category) => {
      const items = allOptions.filter((option) =>
        option.category ? isSameRef(option.category, category) : false
      );
      return {
        group: category.name,
        items: sortBy(items, (option) => option.name.toLowerCase()),
      };
    });
    const uncategorized = allOptions.filter(
      (option) =>
        !option.category ||
        !categories.find((category) =>
          option.category ? isSameRef(option.category, category) : false
        )
    );
    const allGroups = [
      ...groups,
      {
        group: 'Uncategorized',
        items: sortBy(uncategorized, (option) => option.name.toLowerCase()),
      },
    ];
    if (allGroups.length) {
      return allGroups;
    }
    return sortBy(groups, (option) => option.group.toLowerCase()).filter(
      (group) => group.items.length > 0
    );
  }
}
